import React, { useContext } from "react";
import PropTypes from "prop-types";
import api from "components/settings/api";
import { GlobalContext } from "../../context/GlobalContext";
import { toast, Bounce } from "react-toastify";
import CustomToastMessage from "../../common/CustomToastMessage";
import NameLink from "../NameLink";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
} from "@mui/material";
import { makeStyles } from'@mui/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 207,
    height: 240,
    backgroundColor: "#ffffff",
    boxShadow: "none",
    border: "1px solid rgba(0, 27, 17, 0.1)",
    borderRadius: 12,
    "@media (max-width: 760px)": {
      margin: "1.5rem",
      maxHeight: "650px",
      overflow: "auto",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },
  avatarDiv: {
    display: "contents",
  },
  avatar: {
    backgroundColor: "#F8C22B",
    height: 80,
    width: 80,
  },
  cardMargin: {
    justifyContent: "center",
  },
  cardHeader: {
    textAlign: "center",
  },
  cardTitle: {
    fontFamily: "Poppins",
    fontWeight: 600,
    fontSize: 20,
    letterSpacing: 0.15,
  },
  cardSubtitle: {
    fontFamily: "Open Sans",
    fontWeight: 400,
    fontSize: 12,
    letterSpacing: 0.15,
  },
  cardCat: {
    fontFamily: "Open Sans",
    fontWeight: 700,
    fontSize: 16,
    color: "#001B11",
  },
  cardText: {
    color: "rgba(0, 27, 17, 0.7)",
    fontFamily: "Open Sans",
    margin: theme.spacing(1),
    fontSize: 14,
    fontWeight: 600,
  },
  accept: {
    fontFamily: "open sans",
    width: "153px",
    height: "28px",
    margin: "5px 0",
    backgroundColor: "rgba(0,0,0,0.0)",
    border: "1px solid #27AA83",
    color: "#27AA83",
  },
  decline: {
    fontFamily: "open sans",
    width: "153px",
    height: "28px",
    margin: "5px 0",
    backgroundColor: "rgba(0,0,0,0.0)",
    border: "1px solid #E4475B",
    color: "#E4475B",
  },
  subTitle: {
    fontSize: "14px",
  },
}));

const Invite = ({ inviteData, inviteType, fetchInviteData, teamId }) => {
  // This context comes from the GlobalContext Wrapper Component
  const appState = useContext(GlobalContext);
  const { setWhatToReload } = appState;
  const classes = useStyles();

  const acceptInvite = () => {
    sendResponse("Accepted");
  };

  const acceptInviteSubTeam = () => {
    sendResponseSubTeam("Accepted");
  };

  const declineInvite = () => {
    sendResponse("Declined");
  };

  // Toast message function
  const notify = (message) =>
    toast.info(<CustomToastMessage message={message} />, {
      autoClose: 2000,
      closeButton: true,
      hideProgressBar: false,
      transition: Bounce,
      position: "bottom-center",
    });

  const handleDelete = async () => {
    try {
      const res = await api.delete(
        `/api/v1/invites/invite/delete/${inviteData.id}/${teamId}`,
      );
      // Toast message
      notify(res.data.msg);
      // Refetch invites sent
      fetchInviteData();
    } catch (err) {
      throw new Error(err);
    }
  };

  const sendResponse = async (userResponse) => {
    try {
      const payload = {
        inviteId: inviteData.id,
        userResponse,
        publicTeamId: inviteData.Team.public_team_id,
      };

      // Make update
      await api.put("api/v1/invites/invite", payload);

      if (userResponse === "Accepted") {
        // Request new teams data to be fetched
        // The method is part of context and is handled in MyTeams.js
        setWhatToReload("teams");
        // Toast message
        notify("You are now part of the team.");
      }

      // Update invites
      fetchInviteData();
    } catch (err) {
      // Toast message
      notify("Oops, looks like that invite was deleted by the team manager.");
      // Refetch data
      fetchInviteData();
    }
  };

  const sendResponseSubTeam = async (userResponse) => {
    try {
      const payload = {
        inviteId: inviteData.id,
        userResponse,
        publicSubTeamId: inviteData.SubTeams.public_team_id,
      };

      // Make update
      await api.put("api/v1/invites/invite/subteam", payload);

      if (userResponse === "Accepted") {
        // Request new teams data to be fetched
        // The method is part of context and is handled in MyTeams.js
        setWhatToReload("teams");
        // Toast message
        notify("You are now part of the team.");
      }

      // Update invites
      fetchInviteData();
    } catch (err) {
      // Toast message
      notify("Oops, looks like that invite was deleted by the team manager.");
      // Refetch data
      fetchInviteData();
    }
  };
  ;
  if (inviteType === "received") {
    // Dont show declined invites on the client side
    if (inviteData.response === "Declined") {
      return null;
    }

    // const {
    //   Team: { inputs },
    // } = inviteData;

    // const teamNeeds = inputs === "" ? "Not specified" : inputs;
    // const teamMission = validator.unescape(inviteData.Team?.mission);
    const teamMembersNum =
      (inviteData.Team?.Memberships.length - inviteData.Team?.Memberships.filter((mem) => mem.status !== "active").length) ||
      (inviteData.SubTeams?.Memberships.length - inviteData.SubTeams?.Memberships.filter((mem) => mem.status !== "active").length);

    const membersString =
      teamMembersNum === 1
        ? `${teamMembersNum} member`
        : `${teamMembersNum} members`;

    return <>
      <Grid container direction="column" justifyContent="center">
        <Card className={classes.root}>
          <CardContent>
            <Grid container className={classes.cardMargin}>
              <Grid item xs={12} className={classes.avatarDiv}>
                <Avatar
                  aria-label="team"
                  className={classes.avatar}
                  component="div"
                  src={
                    inviteData.Team?.picture || inviteData.SubTeams?.picture
                  }
                />
              </Grid>
              <Grid item xs={12} className={classes.cardHeader}>
                <Typography variant="h5" className={classes.cardTitle}>
                  {inviteData.Team?.title || inviteData.SubTeams?.title}
                </Typography>
                {inviteData.SubTeam?.Memberships.length > 0 ? (
                  <span className={classes.subTitle}>
                    {" "}
                    (Sub Team of {inviteData.SubTeams?.Team.title})
                  </span>
                ) : (
                  <p></p>
                )}
              </Grid>
              {inviteData.response !== "Declined" && (
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  className={classes.buttonContainer}
                >
                  <Grid item>
                    {/* Team Accept Button */}
                    {inviteData.Team?.Memberships.length > 0 && (
                      <Button
                        onClick={acceptInvite}
                        variant="outlined"
                        className={classes.accept}
                        aria-label="accept team invite"
                      >
                        Accept
                      </Button>
                    )}
                    {/* Sub Team Accept Button */}
                    {inviteData.SubTeams?.Memberships.length > 0 && (
                      <Button
                        onClick={acceptInviteSubTeam}
                        variant="outlined"
                        className={classes.accept}
                        aria-label="accept sub-team invite"
                      >
                        Accept
                      </Button>
                    )}
                  </Grid>
                  <Grid item>
                    {/* Team Cancel Button */}
                    {inviteData.Team?.Memberships.length > 0 && (
                      <Button
                        onClick={declineInvite}
                        variant="outlined"
                        className={classes.decline}
                        aria-label="decline team invite"
                      >
                        Decline
                      </Button>
                    )}
                    {/* Sub Team Cancel Button */}
                    {inviteData.SubTeams?.Memberships.length > 0 && (
                      <Button
                        // onClick={declineInvite}
                        variant="outlined"
                        className={classes.decline}
                        aria-label="decline sub-team invite"
                      >
                        Decline
                      </Button>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </>;
  }

  if (inviteType === "sent") {
    return (
      <div className="container--table_row">
        <NameLink userData={inviteData.User} />
        <p className="text text--table_data">{inviteData.response}</p>
        <button
          onClick={handleDelete}
          className="button button--dashboard-small button--dashboard-secondary table_button table_column_four"
          aria-label="delete invite"
        >
          Delete
        </button>
      </div>
    );
  }
};

export default Invite;

Invite.propTypes = {
  inviteData: PropTypes.object,
  inviteType: PropTypes.string,
  fetchInviteData: PropTypes.func,
  teamId: PropTypes.string,
};
